import { useEffect, useRef, useState } from 'react';
import ImageGallery from 'react-image-gallery';
import styles from './PatientFiles.module.scss';
import { Button, Icon, RolePermissionChecker, Text } from 'components';
import { appActions } from 'store/app/slice';
import { selectCurrentPatientCase } from 'store/patients/selectors';
import { useDispatch, useSelector } from 'store/utils';
import { TreatmentPlanTask, UserRole } from 'types';

const allowToEditFiles = [
  TreatmentPlanTask.TO_SUBMIT,
  TreatmentPlanTask.DIAGNOSIS_TO_DO,
  TreatmentPlanTask.DIAGNOSIS_SUBMITTED,
  TreatmentPlanTask.LETTER_TO_DO,
  TreatmentPlanTask.LETTER_SUBMITTED,
  TreatmentPlanTask.LETTER_SENT,
];

export const PatientFiles = () => {
  const [showGallery, setShowGallery] = useState(false);
  const galleryRef = useRef<ImageGallery>(null);
  const galleryStartIndexRef = useRef(0);

  const dispatch = useDispatch();
  const currentPatient = useSelector(selectCurrentPatientCase);
  const patientFiles = currentPatient?.files || [];

  const isAllowedToEditFiles =
    currentPatient && !currentPatient.isTaskBlocked && allowToEditFiles.includes(currentPatient.task);

  useEffect(() => {
    if (showGallery) {
      document.body.style.overflowY = 'hidden';
    } else {
      document.body.style.overflowY = 'auto';
    }
  }, [showGallery]);

  const galleryImages = patientFiles.reduce(
    (arr, file) => {
      const galleryImage = { original: file.file.url, thumbnail: file.file.url };
      arr.push(galleryImage);
      return arr;
    },
    [] as Array<{ original: string }>,
  );

  const handleAddImageButtonPress = () => {
    dispatch(appActions.setIsModalOpened({ name: 'uploadPatientFiles', isOpened: true }));
  };

  const handleImageClick = (index: number) => {
    setShowGallery(true);
    galleryStartIndexRef.current = index;
  };

  const handleGalleryImageClick = (event: any) => {
    if (event?.target?.currentSrc) {
      window.open(event.target.currentSrc);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.blockHeader}>
        <div className={styles.textContainer}>
          <Text type="overline" className={styles.label}>
            Images
          </Text>
          <Text type="caption" className={styles.description}>
            Please provide images to patient case
          </Text>
        </div>
        {isAllowedToEditFiles && (
          <RolePermissionChecker rolesAllowedToView={[UserRole.CLINIC]}>
            <Button onClick={handleAddImageButtonPress} isSmall isLink={!!patientFiles.length}>
              <Icon
                name={patientFiles.length ? 'Edit' : 'Plus'}
                className={patientFiles.length ? styles.editIcon : styles.plusIcon}
              />
            </Button>
          </RolePermissionChecker>
        )}
      </div>
      {!!patientFiles.length && (
        <div className={styles.imageContainer}>
          {patientFiles.slice(0, 6).map((file, index) => (
            <img key={file.file.id} src={file.file.url} alt={file.file.name} onClick={() => handleImageClick(index)} />
          ))}
        </div>
      )}
      {showGallery && (
        <div className={styles.galleryContainer}>
          <ImageGallery
            ref={galleryRef}
            items={galleryImages}
            additionalClass={styles.gallery}
            showNav={false}
            showPlayButton={false}
            showFullscreenButton={false}
            startIndex={galleryStartIndexRef.current}
            onClick={handleGalleryImageClick}
          />
          <Icon name="Close" className={styles.closeGalleryButton} onClick={() => setShowGallery(false)} />
        </div>
      )}
    </div>
  );
};
